import type { SupabaseClient } from '@supabase/supabase-js';
import { createLogger } from '@/utils/logger/devLogger';
import { isLatePaymentAfterArchiveNote } from '@/utils/athletes/latePaymentAfterArchive';
import { fetchAllInChunks } from '@/utils/supabase/fetchInChunks';
import { byCreatedAtDesc } from '@/utils/formatters/byCreatedAtDesc';

const logger = createLogger('ArchiveReasonMetadata');

export interface ArchiveReasonRecord {
  archiveReasonCategory: string | null;
  archiveReasonDetails: string | null;
  archivedAt: string | null;
  // True when the nightly status job (#835, changed_by_role='system') set the
  // inactive flag — that's a lapse, not a staff archive.
  archivedBySystem: boolean;
}

/**
 * Latest archive reason per CURRENTLY-archived athlete (#1629), from the most
 * recent athlete_status_history row that set them inactive. Callers pass only
 * the archived subset: a reactivated athlete's old archive reason is history,
 * not a current label — and the small id set keeps this far from PostgREST's
 * 1000-row response cap.
 */
export async function fetchArchiveReasons(
  archivedAthleteIds: string[],
  organizationId: string,
  client: SupabaseClient,
  toDateOnly: (value: string | null | undefined) => string | null
): Promise<Map<string, ArchiveReasonRecord>> {
  const reasons = new Map<string, ArchiveReasonRecord>();
  if (!archivedAthleteIds.length) return reasons;

  // Chunked AND paged (#2669): "the archived subset" can still be most of a
  // large gym's roster, so the id list overflows the request URL; and this is a
  // history ledger — one athlete can have many inactive rows, so a 100-athlete
  // chunk can pass PostgREST's 1,000-row cap, which truncates silently. A
  // truncated read here would drop the newest archive row for the athletes at
  // the tail and mislabel a staff archive as a system lapse.
  const { rows, error } = await fetchAllInChunks<any>(archivedAthleteIds, (chunk, from, to) => client
    .from('athlete_status_history')
    .select('athlete_id, reason_category, reason_details, created_at, changed_by_role')
    .eq('organization_id', organizationId)
    .in('athlete_id', chunk)
    .eq('new_status', 'inactive')
    .order('id', { ascending: true })
    .range(from, to));

  if (error) {
    // Reason display is decoration on top of the archived/lapsed split — a
    // failed read should degrade to reason-less pills, not break the list.
    logger.error('Error loading archive reasons:', error);
    return reasons;
  }

  // Paging forces `id` order on the query, so restore newest-first here — the
  // "first row per athlete wins" loop below depends on it.
  const data = [...rows].sort(byCreatedAtDesc);

  // Rows come newest-first; keep the first per athlete (the archive that put
  // them in their current state). Skip the #826 late-payment-after-archive
  // NOTE rows first — they ride this same ledger (new_status='inactive',
  // role='system') but record a payment landing after an archive, not the
  // archive itself. Without this, a staff-archived family whose late invoice
  // later settles would read archivedBySystem=true and flip to "Lapsed" —
  // putting e.g. a removed-from-gym family on the win-back list.
  data.forEach((row: any) => {
    if (reasons.has(row.athlete_id) || isLatePaymentAfterArchiveNote(row)) return;
    reasons.set(row.athlete_id, {
      archiveReasonCategory: row.reason_category ?? null,
      archiveReasonDetails: row.reason_details ?? null,
      archivedAt: toDateOnly(row.created_at),
      archivedBySystem: row.changed_by_role === 'system',
    });
  });

  return reasons;
}
